import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { track } from '../../util/analytics';

import css from './VerifiedBadge.module.css';

/**
 * Gezacktes Siegel mit Haken — bewusst NICHT der schlichte Haken-im-Kreis, den
 * unsere eigenen Erfolgsmeldungen schon tragen (IconSuccess) und den jede zweite
 * Oberflaeche fuer "erledigt" benutzt. Der Zackenrand liest sich als
 * ausgestellter Nachweis und ist auf einen Blick davon zu unterscheiden.
 *
 * Die Kontur ist GERECHNET, nicht gezeichnet: 12 Spitzen auf Radius 11.4, dazwischen
 * je ein quadratischer Bogen, dessen Kontrollpunkt so liegt, dass die Kurve durch
 * das Tal auf Radius 8.8 laeuft (B(0.5) = (P0 + 2C + P2) / 4). Ein Bogen pro Zacke
 * statt einer abgetasteten Wellenkurve: dieselbe Silhouette in 222 statt 799
 * Zeichen, und das Zeichen steckt inline in JEDER Suchkarte.
 *
 * Zackenzahl oder Tiefe aendern heisst die Formel neu rechnen, nicht an den
 * Koordinaten schrauben.
 *
 * Inline, weil das Zeichen an den Titel gebunden ist und ein nachgeladenes Icon
 * dort sichtbar nachspringen wuerde. `focusable="false"` gehoert dazu: ohne das
 * Attribut nimmt Edge-Legacy SVGs in die Tabreihenfolge auf, und auf einer
 * Suchergebnisseite lieferte das pro Karte einen leeren Tabstopp.
 */
const CheckIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M12 0.6Q13.7 5.6 17.7 2.1Q16.7 7.3 21.9 6.3Q18.4 10.3 23.4 12Q18.4 13.7 21.9 17.7Q16.7 16.7 17.7 21.9Q13.7 18.4 12 23.4Q10.3 18.4 6.3 21.9Q7.3 16.7 2.1 17.7Q5.6 13.7 0.6 12Q5.6 10.3 2.1 6.3Q7.3 7.3 6.3 2.1Q10.3 5.6 12 0.6Z" />
    <path
      className={css.check}
      d="M7.4 12.3l3.1 3.1 6.1-6.3"
      fill="none"
      stroke="#fff"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * VerifiedBadge
 *
 * Zeichen dafuer, dass Identitaet und Auszahlungskonto des Anbieters ueber
 * Stripe geprueft sind. Es sagt NICHTS ueber die Qualitaet des Raums — die
 * Microcopy ist bewusst eng gehalten, weil ein Vertrauenszeichen sonst als
 * Guetesiegel fuer den Space gelesen wird, das niemand vergeben hat.
 *
 * Zwei Groessen:
 *   small  (14px) fuer ListingCards
 *   medium (16px) fuer die Detailseite
 *
 * Interaktiv nur, wenn `withTooltip` gesetzt ist. Auf Karten MUSS es aus
 * bleiben: die ganze Karte ist ein <a>, und ein <button> darin waere
 * verschachtelte Interaktion — Screenreader kuendigen den Link dann falsch an
 * und der Tastaturfokus bekommt pro Karte einen zusaetzlichen Halt. Die
 * Bedeutung tragen dort `aria-label` und `title`.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className zusaetzliche Klassen
 * @param {'small'|'medium'} [props.size='small']
 * @param {boolean} [props.withTooltip=false] Tooltip als Button (nur ausserhalb von Links)
 * @param {boolean} [props.showLabel=false] sichtbares Wort neben dem Zeichen
 * @param {string?} props.placement Flaeche fuer das Analytics-Ereignis (z. B. 'listing_title')
 * @returns {JSX.Element}
 */
const VerifiedBadge = props => {
  const { className, size = 'small', withTooltip = false, showLabel = false, placement } = props;
  const intl = useIntl();
  // Drei getrennte Gruende statt eines einzelnen `open`-Schalters. Mit einem
  // Schalter kollidierten Maus und Zeigegeraet: das Ueberfahren oeffnete den
  // Tooltip, und der unmittelbar folgende Klick klappte ihn als Umschalter
  // sofort wieder zu — wer draufklickte, sah nichts.
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = hovered || focused || pinned;

  // useId statt einer laufenden Nummer: die ID muss zwischen SSR-Markup und
  // erstem Client-Render identisch sein, sonst wirft React #418.
  const tooltipId = `${useId()}-verified-tooltip`;

  const label = intl.formatMessage({ id: 'VerifiedBadge.label' });
  const description = intl.formatMessage({ id: 'VerifiedBadge.tooltip' });

  // Genau eine Meldung pro OEffnung, egal ueber welchen der drei Wege — sonst
  // zaehlte ein Ueberfahren mit anschliessendem Klick doppelt.
  const wasOpen = useRef(false);
  useEffect(() => {
    // Nur mit `placement` melden. /styleguide ist eine PRODUKTIONSROUTE
    // (routeConfiguration.js), und die Beispiele dort mounten erfundene Nutzer —
    // ohne diese Bedingung feuerte jeder interne Styleguide-Besuch echte
    // GA4-, Reddit- und OpenAI-Ereignisse und verwaesserte genau die Quote,
    // fuer die es sie gibt. Eine Instanz ohne Flaeche ist keine Messflaeche.
    if (open && !wasOpen.current && placement) {
      track.badgeTooltipOpen({ placement });
    }
    wasOpen.current = open;
  }, [open, placement]);

  // Der Tooltip haengt absolut an `left: 0` des Triggers. Steht der rechts in der
  // Zeile — auf 375px Breite wickelt die Metazeile ihn dorthin —, lief das Feld
  // ueber den Bildschirmrand hinaus, und `html/body { overflow-x: clip }` schluckt
  // den Ueberhang, statt scrollbar zu machen: der Satz endete mitten im Wort.
  // Deshalb nach dem OEffnen messen und in den Viewport schieben.
  const tooltipRef = useRef(null);
  useEffect(() => {
    const el = tooltipRef.current;
    if (!open || !el) {
      return;
    }
    el.style.transform = '';
    const rect = el.getBoundingClientRect();
    const gutter = 8;
    const overRight = rect.right - (window.innerWidth - gutter);
    const overLeft = gutter - rect.left;
    const shift = overRight > 0 ? -overRight : overLeft > 0 ? overLeft : 0;
    el.style.transform = shift ? `translateX(${Math.round(shift)}px)` : '';
  }, [open]);

  const dismiss = useCallback(() => {
    setPinned(false);
    setHovered(false);
    // Auch den Fokusgrund zuruecknehmen: sonst bliebe der Tooltip nach Escape
    // stehen, solange der Knopf den Fokus haelt — also genau dann, wenn ihn
    // jemand mit der Tastatur schliessen wollte.
    setFocused(false);
  }, []);

  const classes = classNames(css.root, css[size], className);
  const glyph = (
    <>
      <CheckIcon className={css.icon} />
      {showLabel ? (
        <span className={css.labelText}>
          <FormattedMessage id="VerifiedBadge.label" />
        </span>
      ) : null}
    </>
  );

  if (!withTooltip) {
    // `title` gibt Mausnutzern denselben Satz, den der Tooltip auf der
    // Detailseite zeigt, ohne ein interaktives Element zu erzeugen.
    return (
      <span
        className={classes}
        role="img"
        /* Nur `description`, nicht `label` davor. Der Tooltip-Satz beginnt in allen vier
           Sprachen bereits mit dem Label ("Verifizierter Anbieter — …"), die Verkettung liess
           einen Screenreader den Namen also doppelt ansagen — auf einer Staedteseite 53 Mal. */
        aria-label={description}
        title={description}
      >
        {glyph}
      </span>
    );
  }

  return (
    <span className={classNames(classes, css.interactive)}>
      <button
        type="button"
        className={css.trigger}
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? tooltipId : undefined}
        onClick={() => {
          // Gegen `pinned` pruefen, NICHT gegen `open`.
          //
          // Vorher kippte der Klick nur `pinned`, waehrend `hovered` und `focused`
          // nach demselben Klick beide true waren: der Tooltip blieb offen, der
          // Knopf wirkte tot, und `aria-expanded` meldete einen Zustand, den er
          // gar nicht herstellen konnte. Gegen `open` zu pruefen waere aber auch
          // falsch — Maus und Finger loesen VOR dem Klick ein mouseenter aus, der
          // Klick haette also sofort wieder zugeklappt, was das Zeigen gerade
          // geoeffnet hat.
          //
          // `pinned` trennt beides sauber: der erste Klick haelt fest, was das
          // Zeigen geoeffnet hat, der zweite raeumt ALLE Gruende ab (dismiss()
          // nimmt auch hovered und focused zurueck) und schliesst wirklich. Das
          // gilt fuer Maus, Tastatur und Touch gleichermassen, ohne den Zeigertyp
          // abzufragen.
          if (pinned) {
            dismiss();
          } else {
            setPinned(true);
          }
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          setPinned(false);
        }}
        // Escape schliesst: ein Tooltip, den man mit der Tastatur oeffnet, aber
        // nur mit der Maus wieder los wird, ist kein zugaengliches Element.
        onKeyDown={e => {
          if (e.key === 'Escape' && open) {
            e.stopPropagation();
            dismiss();
          }
        }}
      >
        {glyph}
      </button>
      {open ? (
        <span className={css.tooltip} id={tooltipId} role="tooltip" ref={tooltipRef}>
          {description}
        </span>
      ) : null}
    </span>
  );
};

export default VerifiedBadge;
